import { useEffect, useRef, useState } from "react";

import { Input } from "@src/shared/components/ui/input";
import { cn } from "@src/shared/lib/utils";

export interface InlineEditableTextProps {
  /** Last-saved value. Shown in read mode and used as the edit-mode draft's starting point. */
  value: string;
  /**
   * Called with the trimmed draft when the user commits an actual change.
   * Never called for an unchanged, empty, or whitespace-only draft.
   */
  onSave: (nextValue: string) => void;
  /** Prevents entering edit mode at all (e.g. while the caller can't persist a change). */
  disabled?: boolean;
  /**
   * Caps the draft length in edit mode. Set this to whatever bound the
   * persistence layer enforces, so a long paste is stopped at the input
   * instead of failing the save with nothing to show the user.
   */
  maxLength?: number;
  placeholder?: string;
  /**
   * Accessible name shared by the read-mode trigger and the edit-mode input.
   * Defaults to `Edit ${value}` so the accessible name contains the visible
   * text (WCAG 2.5.3 Label in Name).
   */
  ariaLabel?: string;
  className?: string;
  textClassName?: string;
  inputClassName?: string;
}

/**
 * Click-to-edit text, presentational only: a `<button>` in read mode, an
 * `<input>` in edit mode. Owns nothing but the local draft and the
 * enter/exit rules below — persistence is entirely the caller's job via
 * `onSave`.
 *
 * - Click (or Enter/Space on the button) enters edit mode with the input
 *   focused and its text selected.
 * - Enter or blur commits the trimmed draft via `onSave` when it's non-empty
 *   and different from `value`; otherwise it just exits edit mode.
 * - Escape discards the draft, reverts to `value`, and exits without ever
 *   calling `onSave`. It also stops propagation so it doesn't get picked up
 *   by an ancestor dismissable layer (e.g. a dialog closing on Escape).
 *
 * The draft is local state that is only re-seeded from `value` when entering
 * edit mode, so an in-flight external update to `value` (a poll, another
 * user's edit) never clobbers what the user is currently typing.
 *
 * Focus handling on exit is intentional, not incidental: Enter and Escape
 * both return focus to the read-mode button, so a keyboard user who tabbed
 * in doesn't get dumped back to `document.body` and have to tab all the way
 * in again. Blur does NOT refocus — the user deliberately moved focus
 * elsewhere (another row, a checkbox), and yanking it back would fight them.
 */
export function InlineEditableText({
  value,
  onSave,
  disabled = false,
  maxLength,
  placeholder,
  ariaLabel,
  className,
  textClassName,
  inputClassName,
}: InlineEditableTextProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Enter and blur both try to commit, and Escape's own exit can itself
  // trigger a blur — this guards so only the first exit of a session wins.
  const settledRef = useRef(false);
  // Set by Enter/Escape right before they exit; read by the effect below on
  // the isEditing true->false transition, then cleared. Left false for a
  // blur-triggered exit, so blur never steals focus back to the button.
  const refocusOnExitRef = useRef(false);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
      return;
    }
    if (refocusOnExitRef.current) {
      refocusOnExitRef.current = false;
      buttonRef.current?.focus();
    }
  }, [isEditing]);

  const startEditing = () => {
    if (disabled) return;
    setDraft(value);
    settledRef.current = false;
    setIsEditing(true);
  };

  const commit = (options?: { refocus?: boolean }) => {
    if (settledRef.current) return;
    settledRef.current = true;
    if (options?.refocus) {
      refocusOnExitRef.current = true;
    }
    const trimmed = draft.trim();
    if (trimmed.length > 0 && trimmed !== value) {
      onSave(trimmed);
    }
    setIsEditing(false);
  };

  const revert = () => {
    settledRef.current = true;
    refocusOnExitRef.current = true;
    setDraft(value);
    setIsEditing(false);
  };

  const accessibleName = ariaLabel ?? `Edit ${value}`;

  if (!isEditing) {
    return (
      <button
        aria-label={accessibleName}
        className={cn(
          "min-w-0 rounded-sm text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1",
          textClassName,
          className,
        )}
        disabled={disabled}
        onClick={startEditing}
        ref={buttonRef}
        type="button"
      >
        {value}
      </button>
    );
  }

  return (
    <Input
      aria-label={accessibleName}
      className={cn(
        "h-7 min-w-0 rounded-[6px] px-2 py-0 text-[14px]",
        inputClassName,
        className,
      )}
      maxLength={maxLength}
      onBlur={() => commit()}
      onChange={(event) => setDraft(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          commit({ refocus: true });
        } else if (event.key === "Escape") {
          event.preventDefault();
          // Keep Escape from also being read by an ancestor dismissable
          // layer (dialog/popover) as "close me".
          event.stopPropagation();
          revert();
        }
      }}
      // The input sits inside a dnd-kit sortable <li>; dnd-kit only wires
      // pointerdown listeners onto the dedicated grip handle (see
      // AgendaGroupedSortableList), so a drag can never start from here.
      // Stopping propagation is defense-in-depth in case that ever changes.
      onMouseDown={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
      placeholder={placeholder}
      ref={inputRef}
      value={draft}
    />
  );
}
